import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Input, Switch } from '@nextui-org/react';
import { ArrowLeft, Beer, Plus, Eye, MapPin, Share2 } from 'lucide-react';
import pubService from '../services/pubService';

const TEAL = '#3FCED2';

const FEATURES = [
  {
    icon: <Beer className="w-6 h-6" />,
    title: 'SPIELSTÄTTEN-PROFIL',
    text: 'Erstelle ein detailliertes Profil für deine Dart-Spielstätte mit allen wichtigen Infos wie Name, Standort, Öffnungszeiten, Spielmöglichkeiten und Speisekarte.',
  },
  {
    icon: <Eye className="w-6 h-6" />,
    title: 'SICHTBARKEIT',
    text: 'Erhöhe die Sichtbarkeit deiner Spielstätte und mache sie für Dartspieler in der Region und darüber hinaus leicht auffindbar.',
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: 'STANDORTBASIERTE SUCHE',
    text: 'Lass deine Spielstätte über die Karten- und Standortsuche entdecken und gewinne neue Gäste in deiner Umgebung.',
  },
  {
    icon: <Share2 className="w-6 h-6" />,
    title: 'SOCIAL MEDIA',
    text: 'Verknüpfe deine Social-Media-Kanäle mit dem Spielstätten-Profil und vergrößere deine Reichweite.',
  },
];

const CreatePubPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [steeldart, setSteeldart] = useState(true);
  const [edart, setEdart] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) { setError('Bitte einen Namen festlegen'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await pubService.createPub({ name, steeldart, edart });
      const created = (res as any)?.data ?? res;
      const id = created?._id ?? created?.id;
      if (id) {
        navigate(`/verwaltung/spielstaette/${id}/bearbeiten`);
      } else {
        navigate('/verwaltung?tab=pubs');
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message || 'Spielstätte konnte nicht erstellt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet><title>Spielstätte erstellen - DartCloud</title></Helmet>

      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/verwaltung?tab=pubs')} className="text-zinc-300 hover:text-white p-1">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold flex-1">Spielstätte erstellen</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        <section className="text-center py-6">
          <div className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-4"
            style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>
            <Beer size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-2">Deine Spielstätte in der DartCloud</h2>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto">
            Mache deine Dart-Spielstätte für die Community sichtbar und gewinne neue Gäste.
          </p>
        </section>

        <section className="space-y-4">
          <Input label="Name" placeholder="z.B. Dart-Café Bullseye" value={name}
            onValueChange={(v) => { setName(v); setError(null); }}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} isRequired />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Steeldart</span>
              <Switch isSelected={steeldart} onValueChange={setSteeldart} />
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">E-Dart</span>
              <Switch isSelected={edart} onValueChange={setEdart} />
            </div>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-3 text-sm">{error}</div>
          )}
          <p className="text-xs text-zinc-500">Weitere Details kannst du nach dem Erstellen bearbeiten.</p>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-semibold">Vorteile</h3>
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${TEAL}22`, color: TEAL }}>{f.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold tracking-wide mb-1">{f.title}</div>
                <p className="text-sm text-zinc-400">{f.text}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button className="w-full" isDisabled={saving || !name.trim()} isLoading={saving} onPress={handleCreate}
            startContent={!saving ? <Plus size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}>Erstellen</Button>
        </div>
      </div>
    </div>
  );
};

export default CreatePubPage;
